import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useMemo,
  ComponentType,
  useEffect,
} from 'react';
import { useSelector } from 'react-redux';
import { User } from '../Api/vendor';
import { currentUserSelector } from '../../Containers/App/selectors';

interface CurrentUserContextType {
  currentUser: User | null;
  setCurrentUser: React.Dispatch<React.SetStateAction<User | null>>;
}

// Create the context with a default value
const CurrentUserContext = createContext<CurrentUserContextType | undefined>(
  undefined,
);

interface CurrentUserProviderProps {
  children: ReactNode;
}

export function CurrentUserProvider({ children }: CurrentUserProviderProps) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const reduxUser = useSelector(currentUserSelector);

  useEffect(() => {
    if (reduxUser && Object.keys(reduxUser).length > 0) {
      setCurrentUser(reduxUser);
    }
  }, [reduxUser]);

  const value = useMemo(
    () => ({ currentUser, setCurrentUser }),
    [currentUser, setCurrentUser],
  );

  return (
    <CurrentUserContext.Provider value={value}>
      {children}
    </CurrentUserContext.Provider>
  );
}

export const useCurrentUser = (): CurrentUserContextType => {
  const context = useContext(CurrentUserContext);
  if (context === undefined) {
    throw new Error('useCurrentUser must be used within a CurrentUserProvider');
  }
  return context;
};

interface WithCurrentUserProps {
  currentUser: User | null;
  setCurrentUser: React.Dispatch<React.SetStateAction<User | null>>;
}

export const withCurrentUser = <P extends WithCurrentUserProps>(
  WrappedComponent: ComponentType<P>,
) =>
  function WithCurrentUser(props: Omit<P, keyof WithCurrentUserProps>) {
    const { currentUser, setCurrentUser } = useCurrentUser();
    return (
      <WrappedComponent
        {...(props as P)}
        currentUser={currentUser}
        setCurrentUser={setCurrentUser}
      />
    );
  };
